import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getWarehouseAutoSavingsDataV2, getWarehouseAutoSavings } from "@/helpers/apis";
import { WarehouseDetails } from "../../types";
import { GET_WAREHOUSE_AUTO_SAVINGS, GET_WAREHOUSE_AUTO_SAVINGS_CONFIG } from "../../constants";
import CombinedAutoTuneSection from "./CombinedAutoTuneSection";

interface WarehouseAutoTuneProps {
  warehouseDetails: WarehouseDetails;
  isSchedulingEnabled: boolean;
  isDynamicSchedule: boolean;
  startDate: Date;
  endDate: Date;
  setSelectedView: (value: string) => void;
  selectedView: string;
}

const WarehouseAutoTune = ({
  warehouseDetails,
  startDate,
  endDate,
  selectedView,
  setSelectedView,
  isSchedulingEnabled,
  isDynamicSchedule,
}: WarehouseAutoTuneProps) => {
  const { instance_id } = useParams<{ instance_id: string }>();
  const [aggregation, setAggregation] = useState("day");
  const [autoManageStatus, setAutoManageStatus] = useState(false);
  const [estimatedSavings, setEstimatedSavings] = useState(0);

  const warehouse_rk = warehouseDetails.warehouse_rk;

  // Get auto manage status
  const { data: autoManageData } = useQuery({
    queryKey: [GET_WAREHOUSE_AUTO_SAVINGS_CONFIG, instance_id, warehouse_rk],
    queryFn: () => getWarehouseAutoSavings(instance_id!, warehouse_rk),
    enabled: !!instance_id && !!warehouse_rk,
  });

  // Handle successful auto manage data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (autoManageData) {
      setAutoManageStatus(autoManageData.is_auto_tune_enabled ?? false);
    }
  }, [autoManageData]);

  // Get auto tune data
  const { data: autoTuneData, isLoading: isAutoTuneLoading } = useQuery({
    queryKey: [
      GET_WAREHOUSE_AUTO_SAVINGS,
      instance_id,
      warehouse_rk,
      startDate,
      endDate,
      aggregation,
    ],
    queryFn: () =>
      getWarehouseAutoSavingsDataV2(instance_id!, warehouse_rk, {
        start_date: dayjs(startDate).format("YYYY-MM-DD"),
        end_date: dayjs(endDate).format("YYYY-MM-DD"),
        aggregation,
      }),
    enabled: !!instance_id && !!warehouse_rk,
  });

  // Handle successful auto tune data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (autoTuneData) {
      const savings = autoTuneData?.top_info?.estimated_annual_savings?.value;
      setEstimatedSavings(typeof savings === "number" ? savings : 0);
    }
  }, [autoTuneData]);

  return (
    <CombinedAutoTuneSection
      data={autoTuneData ?? null}
      isLoading={isAutoTuneLoading}
      startDate={startDate}
      endDate={endDate}
      autoManageStatus={autoManageStatus}
      warehouseRK={warehouse_rk}
      estimatedSavings={estimatedSavings}
      aggregation={aggregation}
      setAggregation={setAggregation}
      warehouse_size={warehouseDetails.warehouse_size}
      warehouse_name={warehouseDetails.warehouse_name}
      selectedView={selectedView}
      setSelectedView={setSelectedView}
      isWarehouseSizingEnabled={isSchedulingEnabled}
      isDynamicSchedule={isDynamicSchedule}
    />
  );
};

export default WarehouseAutoTune;
